import { AlertFactory, AlertState } from 'alerts/Alert'
import { NotFound } from 'fejl'
import { Permission } from 'iam/dtos/permissions'
import Member from 'iam/models/Member'
import Workspace from 'iam/models/Workspace'
import link from 'infra/link'
import { SyncWorkspaceFilter } from 'jobs/SyncWorkspaceFilter'
import { action, computed, makeObservable, observable } from 'mobx'
import { task } from 'mobx-task'
import { createTransformer } from 'mobx-utils'
import { from, merge, Observable, Subscription } from 'rxjs'
import {
  distinctUntilChanged,
  filter,
  find,
  map,
  share,
  tap,
} from 'rxjs/operators'
import { Id, Router, SystemNotification } from 'types/definitions'
import { toStream } from 'utils/mobx-util'
import { shareLatest, tailMap } from 'utils/rx-util'
import { filterIncomingOpportunities } from './filter-incoming-opportunities'
import OpportunitiesSearchViewModel from './OpportunitiesSearchViewModel'
import Opportunity from './Opportunity'
import OpportunityDetailsViewModel from './OpportunityDetailsViewModel'
import OpportunityViewModel from './OpportunityViewModel'
import {
  IamPort,
  JobStatsPort,
  ListOpportunitiesDelegate,
  OpportunitiesPort,
} from './ports'
import { validate as isValidUUID } from 'uuid'
import OverachieverViewModel from './views/OverachieverModal/OverachieverViewModel'
import { orderBy, sum } from 'lodash'
import { GetCurrentMember } from 'iam/SessionStore'
import JobEvent from 'jobs/JobEvent'
import { JobEventType } from 'jobs/dtos/job-event'
import { ProOpenJobCount } from 'jobs/job-stats/ProOpenJobCount'
import UserDataStore from '../iam/UserDataStore'
import WorkspaceMembershipStore from '../iam/WorkspaceMembershipStore'
import ConsultationModalViewModel from './views/ConsultationModal/ConsultationModal'
import { OpportunityOfferStore } from './offers/OpportunityOfferStore'
import { OpportunityOffersViewModel } from './offers/OpportunityOffersViewModel'
import { getFeatureToggleClient } from '../infra/featureToggles'
import { ProviderActivePtinStatusDto } from '../iam/models/ProviderActivePtinStatusDto'
import { UserDataType } from '../iam/models/UserDataType'

/**
 * Opportunities view model.
 */
export default class OpportunitiesViewModel {
  /**
   * Subscriber for Rx stuff.
   */
  private subscription = new Subscription()

  /**
   * When an Opportunity becomes available for pickup,
   * show a notification that, when clicked, selects the Opportunity.
   */
  private readonly newOpportunityNotifications$ =
    this.opportunitiesPort.events$.pipe(
      filterIncomingOpportunities(),
      map((op): SystemNotification => {
        const removed$ = this.opportunitiesPort.events$.pipe(
          find((e) => e.type === 'OpportunityRemoved' && e.opportunity === op),
        )
        return {
          icon: op.workspace.branding.logos.icon,
          // Since we auto-close the notification when the opportunity is removed anyway,
          // we can set a high timeout.
          timeout: 999999999,
          title: `New Opportunity Available!`,
          body: `[${op.workspace.name}] ${op.name || 'No description'}`,
          close$: removed$,
          onClick: () =>
            this.detailsVM.select(this._createOpportunityViewModel(op)),
        }
      }),
    )

  /**
   * Fetcher for opportunities.
   */
  private _fetchOpportunities: ListOpportunitiesDelegate = (query) =>
    this.opportunitiesPort.query({
      ...query,
      orderBy: { transmitted: 'asc' },
    })

  /**
   * Transformer for Opportunities.
   */
  private _createOpportunityViewModel = createTransformer<
    Opportunity,
    OpportunityViewModel
  >((op) => new OpportunityViewModel(this, op))

  /**
   * Whether the Opportunities screen is currently the active page.
   * This allows us to not show the global slide-out when viewing the page.
   */
  @observable
  isActive = false

  /**
   * User open jobs count.
   */
  @observable
  proOpenJobCounts = new Map<Id, number>()

  /**
   * Opportunity searcher.
   */
  searcher = new OpportunitiesSearchViewModel(
    this._fetchOpportunities,
    this.opportunitiesPort.events$,
  )

  /**
   * Details view model.
   */
  detailsVM = new OpportunityDetailsViewModel(
    this.opportunitiesPort,
    this.newAlert,
    this.router,
    this.getCurrentMember,
    this.canPickUpOpportunityAfterMemberDataRefresh.bind(this),
    this.consultationModalViewModel,
  )

  /**
   * Dialog for overachiever modal.
   */
  overachieverModalVM = new OverachieverViewModel(this.router)

  /**
   * View model for opportunity offers (dispatch routing).
   */
  offersVM = new OpportunityOffersViewModel(
    this.opportunityOffersStore,
    this.consultationModalViewModel,
    this.opportunitiesPort,
    this.newAlert,
    this.router,
  )

  /**
   * System notifications observable.
   */
  readonly systemNotifications$ = merge(
    this.newOpportunityNotifications$,
    this.offersVM.systemNotifications$,
  ).pipe(share())

  /**
   * Constructor.
   */
  constructor(
    private opportunitiesPort: OpportunitiesPort,
    private jobStatsPort: JobStatsPort,
    private iamPort: IamPort,
    private getCurrentMember: GetCurrentMember,
    private newAlert: AlertFactory,
    private router: Router,
    private jobEvents$: Observable<JobEvent>,
    currentMember$: Observable<Member>,
    private userDataStore: UserDataStore,
    private workspaceMembershipStore: WorkspaceMembershipStore,
    private opportunityOffersStore: OpportunityOfferStore,
    public consultationModalViewModel: ConsultationModalViewModel,
  ) {
    makeObservable(this)
    SyncWorkspaceFilter(this.searcher, currentMember$).subscribe()
  }

  /**
   * Computes whether an active PTIN is required for the current member.
   *
   * @returns {boolean} Returns true if an active PTIN is required, otherwise false.
   */
  @computed
  get ptinRequired(): boolean {
    const featureEnabled = getFeatureToggleClient().variation(
      'Worklayer.RequireActivePTIN',
      false,
    )
    if (!featureEnabled) {
      return false
    }

    const userData = this.userDataStore.getByPublicId(
      this.currentMember.user.publicId,
    )

    return (
      userData?.userDataType === UserDataType.Pro &&
      userData?.ptinStatus === ProviderActivePtinStatusDto.Inactive
    )
  }

  /*
   * Current member
   */
  @computed
  get currentMember() {
    return this.getCurrentMember()
  }

  /**
   * Available opportunities.
   */
  @computed
  get opportunities() {
    return this.searcher.opportunities.map(this._createOpportunityViewModel)
  }

  @computed
  get globalJobCapacity() {
    return (
      this.userDataStore.getByPublicId(this.currentMember.user.publicId)
        ?.jobCapacity ?? 0
    )
  }

  /**
   * All of the current user's workspaces.
   */
  @computed
  get currentUserWorkspaces() {
    return this.workspaceMemberships.map((m) => m.workspace)
  }

  /**
   * Workspace memberships.
   */
  @computed
  get workspaceMemberships() {
    return this.iamPort.getCurrentUserWorkspaceMemberships()
  }

  /**
   * Total Open jobs
   */
  @computed
  get totalOpenJobs() {
    return sum(Array.from(this.proOpenJobCounts.values()))
  }

  /**
   * Workspaces where the capacity has been reached or exceeded.
   */
  @computed
  get workspacesWithoutPickUpCapacity() {
    return this.workspaceMemberships
      .filter((x) => {
        const openJobCount = this.proOpenJobCounts.get(x.workspace.id)
        const totalOpenJobs = openJobCount !== undefined ? openJobCount : 0

        return (
          x.member.maxConcurrentProviderJobs !== 0 &&
          totalOpenJobs >= x.member.maxConcurrentProviderJobs
        )
      })
      .map((x) => x.workspace)
  }

  /**
   * Whether we can pick up opportunities in the currently selected workspace.
   */
  @computed
  get canPickUpOpportunitiesInCurrentWorkspace() {
    const workspaceFilter = this.searcher.workspaceFilter
    if (workspaceFilter.type === 'AllWorkspaces') {
      return true
    }

    return !this.workspacesWithoutPickUpCapacity.some(
      (x) => x.id === workspaceFilter.workspace.id,
    )
  }

  /**
   * Whether we can pick up an opportunity at all.
   */
  @computed
  get canPickUp() {
    return (
      this.canPickUpOpportunityGlobally &&
      this.canPickUpOpportunitiesInCurrentWorkspace
    )
  }

  /**
   * Does what it says on the tin.
   */
  @computed
  get workspacesWhereUserCanViewOpportunities() {
    return orderBy(
      this.iamPort
        .getCurrentUserWorkspaceMemberships()
        .filter(({ member, workspace }) => {
          return (
            member.hasPermission('VIEW_OPPORTUNITIES') ||
            this.getTeamMembersInWorkspaceWhereUserHasSomePermissions(
              workspace,
              ['VIEW_OPPORTUNITIES'],
            ).length > 0
          )
        })
        .map((m) => m.workspace),
      (ws) => ws.name.toLowerCase(),
      'asc',
    )
  }

  /**
   * Whether the user can pick up opportunity globally
   */
  @computed
  get canPickUpOpportunityGlobally() {
    return (
      this.globalJobCapacity === 0 ||
      this.totalOpenJobs < this.globalJobCapacity
    )
  }

  /**
   * Gets the teams in the specified workspace that the user is a member of,
   * and where the user has some of the given permissions.
   *
   * @param workspace
   * @param permissions
   */
  getTeamMembersInWorkspaceWhereUserHasSomePermissions(
    workspace: Workspace,
    permissions: Permission[],
  ) {
    const teamMembers = this.iamPort.getCurrentUserTeamMembers(workspace)
    return teamMembers.filter((tm) => tm.hasSomePermissions(...permissions))
  }

  /**
   * Activate the view model.
   */
  @action.bound
  async activate() {
    this.isActive = true

    // An observable that emits `true` when the probe finishes
    // and concludes that we are allowed to view Opportunities.
    // If we are not allowed, it does not emit anything.
    const canViewOps$ = from(this.probeCanViewOpportunities()).pipe(
      find((canView) => canView),
      shareLatest(),
    )

    // Ensure we always reset to All Opportunities on load.
    this.searcher.selectWorkspaceForFiltering(Workspace.none)

    // Activate the searcher when we conclude that we can view Ops.
    this.subscription = merge(
      // Activate the searcher once we determine whether we can view opportunities.
      canViewOps$.pipe(tap(() => this.searcher.activate())),

      // Set up the capacity refresh pipeline.
      this.refreshJobCountsOnChange(),

      // Whenever we observe a change to our ability to pick up jobs
      // at the global level, display the overachiever dialog once.
      toStream(() => this.canPickUpOpportunityGlobally, true).pipe(
        distinctUntilChanged(),
        tap((canPickupGlobally) =>
          canPickupGlobally
            ? this.overachieverModalVM.close()
            : this.overachieverModalVM.showOnce(),
        ),
      ),
    ).subscribe()
  }

  /**
   * Views an opportunity by ID.
   *
   * @param opportunityId
   */
  async viewOpportunity(opportunityId: string) {
    if (!isValidUUID(opportunityId)) {
      this.newAlert()
        .withState(AlertState.Warning)
        .autoDismiss()
        .saying('Uh-oh', 'That was not a valid opportunity ID')
        .show()

      this.router.push(link.opportunities())

      return
    }

    try {
      const opportunity =
        await this.opportunitiesPort.getOpportunity(opportunityId)

      this.detailsVM.select(this._createOpportunityViewModel(opportunity))
    } catch (error) {
      if (error instanceof NotFound) {
        this.newAlert()
          .withState(AlertState.Warning)
          .autoDismiss()
          .saying(
            'The opportunity has vanished',
            'The opportunity you are trying to view is no longer available',
          )
          .show()

        this.router.push(link.opportunities())
        return
      }

      throw error
    }
  }

  /**
   * Whether the user can pick up an opportunity,
   * will refresh user data before checking so.
   *
   * @param opportunity
   */
  async canPickUpOpportunityAfterMemberDataRefresh(opportunity: Opportunity) {
    await Promise.all([
      this.userDataStore.fetchByPublicId(
        this.currentMember.user.publicId,
        true,
      ),
      this.workspaceMembershipStore.fetchWorkspaceMemberships(true),
    ])

    return this.canPickUpOpportunity(opportunity)
  }

  /**
   * Whether the user can pick up an opportunity.
   *
   * @param opportunity
   */
  canPickUpOpportunity(opportunity: Opportunity) {
    return (
      this.canPickUpOpportunityGlobally &&
      !this.workspacesWithoutPickUpCapacity.some(
        (x) => x.id === opportunity.workspace.id,
      )
    )
  }

  /**
   * Creates a pipeline that will refresh the job counts based on
   * the workspace filter + any time we observe a status change for any job.
   *
   * @private
   */
  private refreshJobCountsOnChange(): Observable<unknown> {
    // The event types that _may_ result in a status change.
    const eventTypes: JobEventType[] = [
      'REOPENED',
      'CLAIMED',
      'CLOSED',
      'REOPENED',
      'TRANSFERRED',
      'FORFEITED',
      'RESUMED',
      'PUT_ON_HOLD',
      'UNCLAIMED',
      'PROVIDER_ADDED',
      'PROVIDER_REMOVED',
    ]

    return merge(
      // When the workspace filter changes (including the initial value)
      this.searcher.workspaceFilter$,
      // Or if we observe an event that may have changed the status
      this.jobEvents$.pipe(filter((e) => eventTypes.includes(e.eventType))),
    ).pipe(
      // Ensures that we fetch sequentially, and that we only do it for
      // the last one if one is currently running.
      tailMap(() => this.fetchProOpenJobCounts(this.currentMember)),
    )
  }

  /**
   * Fetches the open job counts for the specified member.
   *
   * @private
   */
  private async fetchProOpenJobCounts(member: Member) {
    return this.jobStatsPort
      .listProOpenJobCounts(member.user.publicId)
      .then(this.setOpenJobCounts)
  }

  /**
   * Sets the open job counts.
   *
   * @private
   */
  @action.bound
  private setOpenJobCounts(proOpenJobCounts: ProOpenJobCount[]) {
    proOpenJobCounts.forEach((x) =>
      this.proOpenJobCounts.set(x.workspaceId, x.total),
    )
  }

  /**
   * Deactivate the view model.
   */
  @action.bound
  deactivate() {
    // Deselect any selected op when leaving the Ops view.
    this.detailsVM.deselect()
    this.isActive = false
    // In this case, we want to reload Ops every single time, as we want to make sure
    // we don't miss any due to socket flakiness. (._.) ~ ︵ ┻━┻
    this.searcher.deactivate()
    this.subscription.unsubscribe()
  }

  /**
   * Probes the API to see if we are allowed to view Ops at all.
   * This is faster than waiting for every workspaces' team members to
   * fetch. We only call this once.
   */
  probeCanViewOpportunities = task(async () =>
    this.opportunitiesPort.probeCanViewOpportunities(),
  )
}
